import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import AppModal from '../ui/AppModal';
import DataTable from '../ui/DataTable';
import RequirementDateRangeFilter from '../ui/RequirementDateRangeFilter';
import { getEmployeeWorkSummary } from '../../services/employeeWorkSummaryService';
import {
  getDefaultDashboardDateRange,
  getPreviousMonthDateRange,
} from '../../utils/requirementDateFilter';
import { formatDisplayDate } from '../../utils/dateFormat';
import { exportToExcel } from '../../utils/excelTransfer';
import {
  EMPLOYEE_WORK_SUMMARY_EXPORT_COLUMNS,
  buildEmployeeWorkSummaryExportRows,
} from '../../utils/employeeWorkSummaryExport';
import { showError, showSuccess } from '../../utils/toast';

const DEFAULT_RANGE = getDefaultDashboardDateRange();

const METRIC_LABELS = {
  reqsAssigned: 'Reqs Assigned',
  profilesSourced: 'Profiles Sourced',
  ornnovaScreenSelected: 'Ornnova Screen Selected',
  l1Select: 'L1 Select',
  l2Select: 'L2 Select',
  offerReleased: 'Offer Released',
  joined: 'Joined',
  declined: 'Declined',
};

function isInvalidDateRange(fromDate, toDate) {
  if (!fromDate || !toDate) return true;
  const from = new Date(fromDate);
  const to = new Date(toDate);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return true;
  from.setHours(0, 0, 0, 0);
  to.setHours(0, 0, 0, 0);
  return from > to;
}

function MetricCell({ value, onClick }) {
  const count = Number(value) || 0;
  if (!onClick || count === 0) {
    return <span className="hr-ews-metric-value">{count}</span>;
  }
  return (
    <button type="button" className="hr-ews-metric-link" onClick={onClick}>
      {count}
    </button>
  );
}

function EmployeeWorkSummarySection({ embedded = false }) {
  const [startDate, setStartDate] = useState(DEFAULT_RANGE.from);
  const [endDate, setEndDate] = useState(DEFAULT_RANGE.to);
  const [employeeId, setEmployeeId] = useState('');
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [appliedFilters, setAppliedFilters] = useState(null);
  const [showReport, setShowReport] = useState(false);
  const [activePeriod, setActivePeriod] = useState('');
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [dateError, setDateError] = useState('');
  const [summary, setSummary] = useState(null);
  const [detailModal, setDetailModal] = useState({
    show: false,
    title: '',
    rows: [],
    mode: 'requirement',
  });

  const loadSummary = useCallback(async (filters) => {
    const { startDate: rangeStart, endDate: rangeEnd, employeeId: rangeEmployeeId } = filters;
    if (isInvalidDateRange(rangeStart, rangeEnd)) {
      setDateError('Start Date cannot be after End Date.');
      return;
    }
    setDateError('');
    setLoading(true);
    setLoadError('');
    try {
      const response = await getEmployeeWorkSummary({
        startDate: rangeStart,
        endDate: rangeEnd,
        employeeId: rangeEmployeeId || undefined,
      });
      if (response.status === 'Error') {
        setLoadError(response.msg || 'Failed to load employee work summary.');
        setSummary(null);
        return;
      }
      setSummary(response);
    } catch (error) {
      console.error('Employee work summary load failed:', error);
      setLoadError(error.message || 'Failed to load employee work summary.');
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!appliedFilters) return;
    loadSummary(appliedFilters);
  }, [appliedFilters, loadSummary]);

  const handleSearch = () => {
    if (isInvalidDateRange(startDate, endDate)) {
      setDateError('Start Date cannot be after End Date.');
      return;
    }
    setDateError('');
    setActivePeriod('');
    setShowReport(true);
    setAppliedFilters({
      startDate,
      endDate,
      employeeId,
    });
  };

  const runPeriodLoad = (range, periodKey) => {
    setStartDate(range.from);
    setEndDate(range.to);
    setEmployeeId('');
    setEmployeeSearch('');
    setActivePeriod(periodKey);
    setShowReport(true);
    setDateError('');
    setAppliedFilters({
      startDate: range.from,
      endDate: range.to,
      employeeId: '',
    });
  };

  const applyPreviousMonth = () => {
    runPeriodLoad(getPreviousMonthDateRange(), 'previous');
  };

  const applyPresentMonth = () => {
    runPeriodLoad(getDefaultDashboardDateRange(), 'present');
  };

  const handleExportExcel = async () => {
    if (!appliedFilters) {
      showError('Load the summary first (Previous Month, Present Month, or Search).');
      return;
    }

    const { startDate: rangeStart, endDate: rangeEnd, employeeId: rangeEmployeeId } = appliedFilters;
    if (isInvalidDateRange(rangeStart, rangeEnd)) {
      showError('Start Date cannot be after End Date.');
      return;
    }

    setExportLoading(true);
    try {
      const response = await getEmployeeWorkSummary({
        startDate: rangeStart,
        endDate: rangeEnd,
        employeeId: rangeEmployeeId || undefined,
      });

      if (response.status === 'Error') {
        showError(response.msg || 'Unable to export employee work summary.');
        return;
      }

      const exportRows = buildEmployeeWorkSummaryExportRows(response.rows);
      exportToExcel(
        `employee-work-summary-${rangeStart}-to-${rangeEnd}.xlsx`,
        EMPLOYEE_WORK_SUMMARY_EXPORT_COLUMNS,
        exportRows,
      );
      showSuccess('Employee work summary exported.');
    } catch (error) {
      console.error('Employee work summary export failed:', error);
      showError(error.message || 'Unable to export employee work summary.');
    } finally {
      setExportLoading(false);
    }
  };

  const filteredEmployeeOptions = useMemo(() => {
    const employeeOptions = summary?.employeeOptions || [];
    const query = employeeSearch.trim().toLowerCase();
    if (!query) return employeeOptions;
    return employeeOptions.filter((option) => option.name.toLowerCase().includes(query));
  }, [summary?.employeeOptions, employeeSearch]);

  const openMetricDetail = async (rowEmployeeId, metricKey) => {
    const count = summary?.rows?.find((row) => row.employeeId === rowEmployeeId)?.[metricKey];
    if (!count) return;

    setLoading(true);
    setLoadError('');
    try {
      const response = await getEmployeeWorkSummary({
        startDate: appliedFilters?.startDate,
        endDate: appliedFilters?.endDate,
        employeeId: rowEmployeeId,
        metric: metricKey,
      });
      if (response.status === 'Error' || !response.detail) {
        setLoadError(response.msg || 'Failed to load detail.');
        return;
      }

      const employeeName = response.detail.employeeName || 'Employee';
      const metricLabel = METRIC_LABELS[metricKey] || metricKey;
      const mode = metricKey === 'reqsAssigned' ? 'requirement' : 'candidate';

      setDetailModal({
        show: true,
        title: `${employeeName} — ${metricLabel}`,
        rows: response.detail.rows || [],
        mode,
      });
    } catch (error) {
      console.error('Employee work summary detail failed:', error);
      setLoadError(error.message || 'Failed to load detail.');
    } finally {
      setLoading(false);
    }
  };

  const requirementColumns = useMemo(() => [
    { key: 'regId', label: 'Req ID', sortable: true },
    { key: 'client', label: 'Client', sortable: true },
    { key: 'role', label: 'Role', sortable: true },
    {
      key: 'assignedDate',
      label: 'Assigned Date',
      sortable: true,
      render: (row) => formatDisplayDate(row.assignedDate),
    },
    {
      key: 'requirementId',
      label: 'Action',
      render: (row) => (
        <Link to={`/Requirments?requirementId=${encodeURIComponent(row.requirementId || '')}`}>
          Open
        </Link>
      ),
    },
  ], []);

  const candidateColumns = useMemo(() => [
    { key: 'candidateName', label: 'Source Profile', sortable: true },
    { key: 'client', label: 'Client', sortable: true },
    { key: 'role', label: 'Role', sortable: true },
    { key: 'regId', label: 'Req ID', sortable: true },
    { key: 'status', label: 'Status', sortable: true, type: 'status', useStoredLabel: true },
    {
      key: 'uploadedOn',
      label: 'Sourced Date',
      sortable: true,
      render: (row) => formatDisplayDate(row.uploadedOn || row.statusEventDate),
    },
    {
      key: 'requirementId',
      label: 'Action',
      render: (row) => (
        <Link to={`/Requirments?requirementId=${encodeURIComponent(row.requirementId || '')}`}>
          View Requirement
        </Link>
      ),
    },
  ], []);

  const rows = summary?.rows || [];
  const emptyMessage = appliedFilters?.employeeId
    ? 'No activity found for this employee in the selected date range.'
    : 'No employee activity found for the selected date range.';

  const content = (
    <>
      {!embedded ? (
        <>
          <h3 className="hr-section-title">Employee Work Summary</h3>
          <p className="hr-page-hint mb-3">
            Reqs Assigned carry forward eligible active assignments through the selected end date. Profiles Sourced and status columns use only dates in the selected range.
          </p>
        </>
      ) : null}

      <div className="hr-employee-work-summary-period-actions mb-3">
        <Button
          type="button"
          variant={activePeriod === 'previous' ? 'primary' : 'outline-primary'}
          className="hr-employee-work-summary-period-btn"
          onClick={applyPreviousMonth}
        >
          Previous Month
        </Button>
        <Button
          type="button"
          variant={activePeriod === 'present' ? 'primary' : 'outline-primary'}
          className="hr-employee-work-summary-period-btn"
          onClick={applyPresentMonth}
        >
          Present Month
        </Button>
        <Button
          type="button"
          variant="outline-success"
          className="hr-employee-work-summary-period-btn"
          onClick={handleExportExcel}
          disabled={exportLoading}
        >
          {exportLoading ? 'Exporting…' : 'Export Excel'}
        </Button>
      </div>

      {!showReport ? (
        <p className="hr-page-hint mb-0">Choose Previous Month or Present Month to load the summary.</p>
      ) : null}

      {showReport ? (
        <>
      <section className="hr-card hr-rtl-req-filters-card mb-3">
        <div className="hr-rtl-req-filter-header">
          <svg
            className="hr-rtl-req-filter-icon"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden
          >
            <path
              d="M4 5h16l-6.5 7.5v5.5L10 20v-7.5L4 5z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinejoin="round"
            />
          </svg>
          <h2 className="hr-rtl-req-filter-title">Filters</h2>
        </div>

        <div className="hr-rtl-req-filter-toolbar">
          <div className="hr-rtl-req-filter-group">
            <span className="hr-rtl-req-filter-label">Date Range</span>
            <RequirementDateRangeFilter
              fromDate={startDate}
              toDate={endDate}
              onFromChange={setStartDate}
              onToChange={setEndDate}
              inline
              hideClearButton
              className="hr-rtl-req-date-range"
            />
          </div>

          <div className="hr-rtl-req-filter-group hr-rtl-req-filter-group-employee">
            <span className="hr-rtl-req-filter-label">Employee</span>
            <div className="hr-rtl-req-employee-fields">
              <div className="hr-rtl-req-search-wrap">
                <input
                  type="search"
                  className="form-control hr-rtl-req-filter-input hr-rtl-req-search-plain"
                  placeholder="Search employees"
                  value={employeeSearch}
                  onChange={(event) => setEmployeeSearch(event.target.value)}
                  aria-label="Search employees"
                />
              </div>
              <select
                className="form-select hr-rtl-req-filter-input hr-rtl-req-employee-select"
                value={employeeId}
                onChange={(event) => setEmployeeId(event.target.value)}
                aria-label="Employee filter"
              >
                <option value="">All Employees</option>
                {filteredEmployeeOptions.map((employee) => (
                  <option key={employee.id} value={employee.id}>
                    {employee.name}
                    {employee.userType === 'TeamLead' ? ' (Team Lead)' : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <button
            type="button"
            className="hr-rtl-req-filter-search-btn"
            onClick={handleSearch}
            disabled={loading}
          >
            Search
          </button>
        </div>

        {dateError ? <div className="alert alert-warning py-2 mb-0 mt-3">{dateError}</div> : null}
      </section>
      {loadError && <div className="alert alert-danger py-2">{loadError}</div>}

      {loading && !summary ? (
        <p className="hr-empty-state">Loading employee work summary…</p>
      ) : null}

      {!loading && rows.length > 0 ? (
        <div className="table-responsive hr-employee-work-summary-wrap">
          <table className="table table-bordered table-hover table-sm align-middle hr-employee-work-summary-table">
            <colgroup>
              <col className="hr-ews-c-recruiter" />
              <col className="hr-ews-c-reqs" />
              <col className="hr-ews-c-profiles" />
              <col className="hr-ews-c-ornnova" />
              <col className="hr-ews-c-l1" />
              <col className="hr-ews-c-l2" />
              <col className="hr-ews-c-offer" />
              <col className="hr-ews-c-joined" />
              <col className="hr-ews-c-declined" />
            </colgroup>
            <thead>
              <tr>
                <th className="hr-ews-th-name" title="Recruiter / TL">
                  <span className="hr-ews-th-label">Recruiter / TL</span>
                </th>
                <th className="hr-ews-th-metric" title="Reqs Assigned">
                  <span className="hr-ews-th-label">
                    Reqs
                    <br />
                    Assigned
                  </span>
                </th>
                <th className="hr-ews-th-metric" title="Profiles Sourced">
                  <span className="hr-ews-th-label">
                    Profiles
                    <br />
                    Sourced
                  </span>
                </th>
                <th className="hr-ews-th-metric" title="Ornnova Screen Selected">
                  <span className="hr-ews-th-label">
                    Ornnova
                    <br />
                    Screen Sel.
                  </span>
                </th>
                <th className="hr-ews-th-metric-narrow" title="L1 Select">
                  <span className="hr-ews-th-label">L1</span>
                </th>
                <th className="hr-ews-th-metric-narrow" title="L2 Select">
                  <span className="hr-ews-th-label">L2</span>
                </th>
                <th className="hr-ews-th-metric" title="Offer Released">
                  <span className="hr-ews-th-label">
                    Offer
                    <br />
                    Released
                  </span>
                </th>
                <th className="hr-ews-th-metric-narrow" title="Joined">
                  <span className="hr-ews-th-label">Joined</span>
                </th>
                <th className="hr-ews-th-metric-narrow" title="Declined">
                  <span className="hr-ews-th-label">Declined</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.employeeId}>
                  <td className="hr-ews-td-name" title={row.name}>
                    <strong className="hr-ews-name">{row.name}</strong>
                  </td>
                  <td className="hr-ews-td-metric">
                    <MetricCell
                      value={row.reqsAssigned}
                      onClick={() => openMetricDetail(row.employeeId, 'reqsAssigned')}
                    />
                  </td>
                  <td className="hr-ews-td-metric">
                    <MetricCell
                      value={row.profilesSourced}
                      onClick={() => openMetricDetail(row.employeeId, 'profilesSourced')}
                    />
                  </td>
                  <td className="hr-ews-td-metric">
                    <MetricCell
                      value={row.ornnovaScreenSelected}
                      onClick={() => openMetricDetail(row.employeeId, 'ornnovaScreenSelected')}
                    />
                  </td>
                  <td className="hr-ews-td-metric-narrow">
                    <MetricCell
                      value={row.l1Select}
                      onClick={() => openMetricDetail(row.employeeId, 'l1Select')}
                    />
                  </td>
                  <td className="hr-ews-td-metric-narrow">
                    <MetricCell
                      value={row.l2Select}
                      onClick={() => openMetricDetail(row.employeeId, 'l2Select')}
                    />
                  </td>
                  <td className="hr-ews-td-metric">
                    <MetricCell
                      value={row.offerReleased}
                      onClick={() => openMetricDetail(row.employeeId, 'offerReleased')}
                    />
                  </td>
                  <td className="hr-ews-td-metric-narrow">
                    <MetricCell
                      value={row.joined}
                      onClick={() => openMetricDetail(row.employeeId, 'joined')}
                    />
                  </td>
                  <td className="hr-ews-td-metric-narrow">
                    <MetricCell
                      value={row.declined}
                      onClick={() => openMetricDetail(row.employeeId, 'declined')}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {!loading && rows.length === 0 && summary && !loadError ? (
        <p className="hr-empty-state">{emptyMessage}</p>
      ) : null}

      {!loading && rows.length > 0 && summary && !summary.hasActivity ? (
        <p className="hr-empty-state mt-2">{emptyMessage}</p>
      ) : null}
        </>
      ) : null}

      <AppModal
        show={detailModal.show}
        onHide={() => setDetailModal({ show: false, title: '', rows: [], mode: 'requirement' })}
        title={detailModal.title}
        size="lg"
      >
        <DataTable
          columns={detailModal.mode === 'requirement' ? requirementColumns : candidateColumns}
          data={detailModal.rows}
          rowKey={detailModal.mode === 'requirement' ? 'requirementId' : 'candidateId'}
          searchPlaceholder={detailModal.mode === 'requirement' ? 'Search requirements' : 'Search profiles'}
          searchKeys={
            detailModal.mode === 'requirement'
              ? ['regId', 'client', 'role']
              : ['candidateName', 'client', 'role', 'regId', 'status']
          }
          emptyMessage="No records for this metric in the selected period."
          pageSize={10}
        />
      </AppModal>
    </>
  );

  if (embedded) {
    return <div className="hr-employee-work-summary">{content}</div>;
  }

  return (
    <section className="hr-card hr-employee-work-summary" style={{ marginTop: '24px' }}>
      {content}
    </section>
  );
}

export default EmployeeWorkSummarySection;
